/**
 * Projects Loader Utility
 * Auto-discovers projects from src/projects/{category}/{slug}/meta.json
 * Uses webpack's require.context for Vue CLI compatibility
 */

// Import all meta.json files using webpack's require.context
const requireContext = require.context("@/projects", true, /meta\.json$/);

/**
 * Parse project path to extract category and slug
 * @param {string} path - Path like "./apps/sample/meta.json"
 * @returns {{ category: string, slug: string }}
 */
function parseProjectPath(path) {
  const match = path.match(/\.\/([^/]+)\/([^/]+)\/meta\.json$/);
  if (match) {
    return { category: match[1], slug: match[2] };
  }
  return null;
}

/**
 * Get all projects with their metadata
 * @returns {Array} Array of project objects with category, slug, and meta data
 */
export function getAllProjects() {
  const projects = [];

  requireContext.keys().forEach((path) => {
    const parsed = parseProjectPath(path);
    if (parsed) {
      const data = requireContext(path);
      projects.push({
        ...data,
        category: parsed.category,
        slug: parsed.slug,
      });
    }
  });

  return projects.sort((a, b) => {
    // If both have order field, sort by it
    if (a.order !== undefined && b.order !== undefined) {
      return a.order - b.order;
    }
    // If only one has order, that one comes first
    if (a.order !== undefined) return -1;
    if (b.order !== undefined) return 1;
    // Otherwise maintain original order
    return 0;
  });
}

/**
 * Get all unique categories
 * @returns {Array} Array of category names
 */
export function getCategories() {
  const categories = new Set(getAllProjects().map((p) => p.category));
  // Return in preferred order
  const order = ["apps", "tools", "games"];
  return order
    .filter((cat) => categories.has(cat))
    .concat([...categories].filter((cat) => !order.includes(cat)));
}

/**
 * Get a single project by category and slug
 * @param {string} category - Category name
 * @param {string} slug - Project slug
 * @returns {Object|null} Project object or null if not found
 */
export function getProject(category, slug) {
  return (
    getAllProjects().find(
      (project) => project.category === category && project.slug === slug,
    ) || null
  );
}
